import React from 'react';

import { lazyComponentLoader } from '~/utils/component-loader/componentLoader';
import { AssetOverview } from '~/views/asset-overview/AssetOverview';
import { ForgotPasswordView } from '~/views/auth/forgotPassword';
import OnHold from '~/views/auth/on-hold';
import { ResetPasswordView } from '~/views/auth/reset-password-view/ResetPasswordView';
import { Verify } from '~/views/auth/verify/Verify';
import { CalendarView } from '~/views/calendar/CalendarView';
import { ServerError } from '~/views/error/server-error/ServerError';
import { Scenario } from '~/views/scenario/Scenario';
import { InteractiveBrokersCallback } from '~/views/settings/portfolio-view/components/interactive-brokers-callback/InteractiveBrokersCallback';

import { Redirect } from '../routes/Redirect';
import { News } from '../views/news/News';
import { appPaths, authPaths } from './app-paths';
import { queryKeys } from './query-params';
import { AppRoute } from './Routes';

const createRoutesFromPathArray = (
  paths: string[],
  route: AppRoute
): AppRoute[] => paths.map(path => ({ path, ...route }));

const Dashboard = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "dashboard" */ '../views/dashboard'
    ),
  'dashboard'
);

const Alfred = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "alfred" */ '../views/alfred'
    ),
  'alfred'
);

const Screener = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "screener" */ '../views/screener'
    ),
  'screener'
);

const TermsOfService = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "termsOfService" */ '../views/terms-of-service'
    ),
  'termsOfService'
);
const Explore = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "explore" */ '../views/explore'
    ),
  'insights'
);

const Onboarding = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "onboarding" */ '../views/onboarding'
    ),
  'onboarding'
);

const TurboChart = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "turboChart" */ '../views/turbo-chart'
    ),
  'TurboChart'
);

const WatchlistPage = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "dashboard" */ '../views/watchlist-page'
    ),
  'watchlist-page'
);

const Settings = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "settings" */ '~/views/settings'
    ),
  'settings'
);
const SignUp = lazyComponentLoader(
  () => import(/* webpackChunkName: "signUp" */ '~/views/auth/sign-up'),
  'signUp'
);
const LoginView = lazyComponentLoader(
  () => import(/* webpackChunkName: "loginView" */ '~/views/auth/login-view'),
  'login-view'
);

const ThemeOverview = lazyComponentLoader(
  () =>
    import(/* webpackChunkName: "themeOverview" */ '~/views/theme-overview'),
  'ThemeOverview'
);

const Stream = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "stream" */ '../views/stream'
    ),
  'stream'
);

const Alerts = lazyComponentLoader(
  () =>
    import(
      /* webpackPrefetch: true */ /* webpackChunkName: "alerts" */ '../views/alerts'
    ),
  'alerts'
);

export const routes: AppRoute[] = [
  {
    path: appPaths.base,
    element: <Dashboard />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.dashboard',
    },
  },
  {
    path: `${appPaths.themeOverview}/:themeId`,
    element: <ThemeOverview />,
  },
  {
    path: appPaths.assetOverview,
    element: <AssetOverview />,
  },
  ...createRoutesFromPathArray(
    [appPaths.watchlist, `${appPaths.watchlist}/:watchlistId`],
    { element: <WatchlistPage /> }
  ),
  {
    path: appPaths.chart,
    element: <TurboChart />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.chart',
    },
  },
  {
    path: appPaths.screener,
    element: <Screener />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.screener',
    },
  },
  {
    path: `${appPaths.screener}/:screenerId`,
    element: <Screener />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.screener',
    },
  },
  {
    path: appPaths.news,
    element: <News />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.news',
    },
  },
  {
    path: appPaths.tos,
    element: <TermsOfService />,
  },
  {
    path: appPaths.insights,
    element: <Explore />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.insights',
    },
  },
  {
    path: appPaths.onboarding,
    element: <Onboarding />,
  },
  {
    path: appPaths.alfred,
    element: <Alfred />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.alfred',
    },
  },

  ...createRoutesFromPathArray(
    [
      appPaths.settings.account,
      appPaths.settings.appearance,
      appPaths.settings.portfolio,
    ],
    { element: <Settings /> }
  ),
  {
    path: `${appPaths.settings.subscription}/*`,
    element: <Settings />,
  },
  {
    path: appPaths.accountOnHold,
    element: <OnHold />,
  },
];

export const authRoutes: AppRoute[] = [
  {
    path: authPaths.login,
    element: <LoginView />,
  },
  {
    path: authPaths.signUp,
    element: <SignUp />,
  },
];

export const commonRoutes: AppRoute[] = [
  {
    path: appPaths.verifyEmail,
    element: <Verify />,
  },
  {
    path: appPaths.error,
    element: <ServerError />,
  },
  ...createRoutesFromPathArray(
    [
      authPaths.forgotPassword,
      `${authPaths.forgotPassword}/:${queryKeys.emailReset}`,
    ],
    {
      element: <ForgotPasswordView />,
    }
  ),
  {
    path: authPaths.resetPassword,
    element: <ResetPasswordView />,
  },
  {
    path: appPaths.ib,
    element: <InteractiveBrokersCallback />,
  },
];

export const nonBasicRoutes: AppRoute[] = [
  {
    path: appPaths.events,
    element: <CalendarView />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.events',
    },
  },
  {
    path: appPaths.scenario,
    element: <Scenario />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.scenario',
    },
  },
];

export const nonBasicOrLightRoutes: AppRoute[] = [
  {
    path: appPaths.stream,
    element: (
      <Redirect from={appPaths.stream} to={`${appPaths.stream}/focus`} />
    ),
  },
  {
    path: `${appPaths.stream}/focus`,
    element: <Stream />,
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.stream',
    },
  },
  {
    path: `${appPaths.stream}/explore`,
    element: <Stream />,
    featureFlag: 'stream_feed_v2',
    crumb: {
      isSidebar: true,
      labelKey: 'common:sideNavigation.stream',
    },
  },
];

export const featureRoutes: AppRoute[] = [
  { path: appPaths.alerts, element: <Alerts />, featureFlag: 'alerts' },
];
